/**
 * ClusterCardView — aggregate performance cards grouped by pillar or funnel stage.
 *
 * Each card shows three metric columns (Discovery, Engagement, Conversion),
 * an impressions sparkline, a position dot strip, and best/worst callouts.
 */

import React, { useEffect, useMemo, useState } from 'react';
import {
  performanceMetricsService,
  ArticlePerformanceRow,
} from '../../../../services/performanceMetricsService';
import { filterByCohort } from './PerformanceDashboard';
import { Sparkline } from './Sparkline';

type GroupBy = 'pillar' | 'funnel_stage';

// Color palette for cluster lines
const CLUSTER_COLORS = [
  '#01bcd4', '#16a34a', '#f59e0b', '#ef4444', '#8b5cf6',
  '#ec4899', '#06b6d4', '#84cc16', '#f97316', '#6366f1',
  '#14b8a6', '#e11d48',
];

// Multi-line chart — renders multiple named series on the same axes
function MultiLineChart({ series, height = 160, label }: {
  series: { name: string; values: number[]; color: string }[];
  height?: number;
  label: string;
}) {
  const containerRef = React.useRef<HTMLDivElement>(null);
  const [width, setWidth] = React.useState(400);
  React.useEffect(() => {
    const el = containerRef.current;
    if (!el) return;
    const obs = new ResizeObserver((entries) => {
      for (const entry of entries) {
        setWidth(entry.contentRect.width);
      }
    });
    obs.observe(el);
    setWidth(el.clientWidth);
    return () => obs.disconnect();
  }, []);

  if (series.length === 0) return null;
  const maxLen = Math.max(...series.map(s => s.values.length));
  const allMax = Math.max(...series.flatMap(s => s.values), 1);
  const padTop = 8;
  const padBottom = 14;
  const padLeft = 40;
  const chartW = width - padLeft;
  const chartH = height - padTop - padBottom;
  const stepX = maxLen > 1 ? chartW / (maxLen - 1) : 0;

  // Y-axis gridlines — pick 4 nice tick values
  const tickCount = 4;
  const rawStep = allMax / tickCount;
  const magnitude = Math.pow(10, Math.floor(Math.log10(rawStep)));
  const niceStep = Math.ceil(rawStep / magnitude) * magnitude;
  const ticks: number[] = [];
  for (let t = 0; t <= allMax; t += niceStep) {
    ticks.push(t);
  }
  if (ticks[ticks.length - 1] < allMax) ticks.push(ticks[ticks.length - 1] + niceStep);
  const scaleMax = ticks[ticks.length - 1] || allMax;

  return (
    <div ref={containerRef}>
      <div className="text-[10px] font-medium text-gray-400 uppercase tracking-wide mb-1">{label}</div>
      <svg width="100%" height={height} viewBox={`0 0 ${width} ${height}`} preserveAspectRatio="none" className="overflow-visible">
        {/* Y-axis gridlines and labels */}
        {ticks.map((t) => {
          const y = padTop + chartH - (t / scaleMax) * chartH;
          return (
            <g key={t}>
              <line x1={padLeft} y1={y} x2={width} y2={y} stroke="#f0f0f0" strokeWidth={0.5} />
              <text x={padLeft - 4} y={y + 3} textAnchor="end" className="text-[9px] fill-gray-400">{fmt(t)}</text>
            </g>
          );
        })}
        {/* X-axis labels */}
        <text x={padLeft} y={height} textAnchor="start" className="text-[8px] fill-gray-300">Day 1</text>
        <text x={width} y={height} textAnchor="end" className="text-[8px] fill-gray-300">Day {maxLen}</text>
        {/* Lines */}
        {series.map((s) => {
          const points = s.values.map((v, i) => {
            const x = padLeft + i * stepX;
            const y = padTop + chartH - (v / scaleMax) * chartH;
            return `${x.toFixed(1)},${y.toFixed(1)}`;
          });
          return (
            <path
              key={s.name}
              d={`M ${points.join(' L ')}`}
              fill="none"
              stroke={s.color}
              strokeWidth={1.5}
              strokeLinejoin="round"
              strokeLinecap="round"
              opacity={0.85}
            />
          );
        })}
      </svg>
      {/* Legend */}
      <div className="flex flex-wrap gap-x-3 gap-y-0.5 mt-1">
        {series.map((s) => (
          <span key={s.name} className="flex items-center gap-1 text-[9px] text-gray-500">
            <span className="w-2 h-2 rounded-full shrink-0" style={{ backgroundColor: s.color }} />
            {s.name.replace(/^Cluster \d+:\s*/, '')}
          </span>
        ))}
      </div>
    </div>
  );
}

// Horizontal bar chart for a single metric
function RankingBarChart({ clusters, getValue, color, label, preserveOrder }: {
  clusters: ClusterData[];
  getValue: (c: ClusterData) => number;
  color: string;
  label: string;
  preserveOrder?: boolean;
}) {
  const sorted = preserveOrder ? clusters : [...clusters].sort((a, b) => getValue(b) - getValue(a));
  const maxVal = Math.max(...sorted.map(getValue), 1);
  return (
    <div>
      <div className="text-[10px] font-medium text-gray-400 uppercase tracking-wide mb-2">{label}</div>
      <div className="space-y-1.5">
        {sorted.map((c) => {
          const val = getValue(c);
          const pct = (val / maxVal) * 100;
          const shortName = (c.name || 'Uncategorized').replace(/^Cluster \d+:\s*/, '');
          return (
            <div key={c.name} className="flex items-center gap-2">
              <div className="w-36 shrink-0 text-[11px] text-gray-600 truncate text-right" title={c.name}>
                {shortName}
              </div>
              <div className="flex-1 bg-gray-50 rounded-full h-4 overflow-hidden">
                <div
                  className="h-full rounded-full transition-all duration-500"
                  style={{ width: `${Math.max(pct, 0.5)}%`, backgroundColor: color }}
                />
              </div>
              <span className="text-[10px] font-mono text-gray-500 w-14 text-right">{fmt(val)}</span>
            </div>
          );
        })}
      </div>
    </div>
  );
}

interface ClusterCardViewProps {
  groupBy: GroupBy;
  cohort?: import('./PerformanceDashboard').CohortFilter;
}

interface ClusterData {
  name: string;
  articles: ArticlePerformanceRow[];
  // Discovery
  totalClicks: number;
  totalImpressions: number;
  totalSessions: number;
  impressionsSeries: number[];
  clicksSeries: number[];
  sessionsSeries: number[];
  clicksDelta7d: string; // e.g. "↑ 12%" or "↓ 5%"
  // Position dot strip
  positions: { title: string; pos: number }[];
  // Engagement
  avgReadTime: number | null; // seconds
  scrollRate: number | null;
  engagementRate: number | null;
  // AI
  totalAiSessions: number;
  aiSourceBreakdown: { source: string; sessions: number }[];
  totalAioImpressions: number;
  totalAioClicks: number;
  // Conversion
  totalConversions: number;
  convRate: number | null;
  // Best/worst
  bestReadArticle: { title: string; time: number } | null;
  worstReadArticle: { title: string; time: number } | null;
  // Quality metrics
  medianClicks: number;
  medianImpressions: number;
  medianSessions: number;
  clicksHitRate: number;
  impressionsHitRate: number;
  // Scorecard
  impressionsPerArticle: number;
  sessionsPerArticle: number;
  clusterCtr: number | null; // clicks / impressions
  lowConfidence: boolean;
  scorecardLabel: string;
  scorecardColor: string;
  scorecardExplanation: string;
}

function computeMedian(values: number[]): number {
  if (values.length === 0) return 0;
  const sorted = [...values].sort((a, b) => a - b);
  const mid = Math.floor(sorted.length / 2);
  return sorted.length % 2 === 0 ? (sorted[mid - 1] + sorted[mid]) / 2 : sorted[mid];
}

function buildClusterData(
  name: string,
  articles: ArticlePerformanceRow[],
  siteMedianClicks: number,
  siteMedianImpressions: number,
  siteMedianImprPerArticle: number,
  siteMedianSessPerArticle: number,
  siteMedianCtr: number,
): ClusterData {
  const totalClicks = articles.reduce((s, a) => s + (a.clicks || 0), 0);
  const totalImpressions = articles.reduce((s, a) => s + (a.impressions || 0), 0);
  const totalSessions = articles.reduce((s, a) => s + (a.ga4_sessions || 0), 0);

  // Aggregate impressions series (sum across articles per day)
  const maxLen = Math.max(...articles.map(a => (a.impressions_series || []).length), 0);
  const impressionsSeries: number[] = [];
  for (let i = 0; i < maxLen; i++) {
    let dayTotal = 0;
    for (const a of articles) {
      dayTotal += (a.impressions_series || [])[i] || 0;
    }
    impressionsSeries.push(dayTotal);
  }

  // 7d vs prior 7d clicks delta
  const clicksSeriesAgg: number[] = [];
  for (let i = 0; i < maxLen; i++) {
    let dayTotal = 0;
    for (const a of articles) {
      dayTotal += (a.clicks_series || [])[i] || 0;
    }
    clicksSeriesAgg.push(dayTotal);
  }
  const last7 = clicksSeriesAgg.slice(-7).reduce((s, v) => s + v, 0);
  const prior7 = clicksSeriesAgg.slice(-14, -7).reduce((s, v) => s + v, 0);
  let clicksDelta7d = '—';
  if (prior7 > 0) {
    const pct = Math.round(((last7 - prior7) / prior7) * 100);
    clicksDelta7d = pct >= 0 ? `↑ ${pct}%` : `↓ ${Math.abs(pct)}%`;
  } else if (last7 > 0) {
    clicksDelta7d = '↑ new';
  }

  // Position dots (only articles with position data)
  const positions = articles
    .filter(a => a.avg_position != null && a.avg_position > 0)
    .map(a => ({ title: a.title, pos: a.avg_position! }))
    .sort((a, b) => a.pos - b.pos);

  // Engagement aggregates (from GA4 fields)
  const withGA4 = articles.filter(a => (a.ga4_data_points || 0) > 0);
  const totalUsers = withGA4.reduce((s, a) => s + ((a as any).ga4_total_users || 0), 0);
  const totalEngDuration = withGA4.reduce((s, a) => s + ((a as any).ga4_engagement_duration || 0), 0);
  const totalScrolled = withGA4.reduce((s, a) => s + ((a as any).ga4_scrolled_users || 0), 0);
  const avgReadTime = totalUsers > 0 ? totalEngDuration / totalUsers : null;
  const scrollRate = totalUsers > 0 ? totalScrolled / totalUsers : null;

  // Engagement rate: weighted average of bounce rates
  let brWeighted = 0;
  let brSessions = 0;
  for (const a of withGA4) {
    const sess = a.ga4_sessions || 0;
    const br = a.ga4_bounce_rate;
    if (sess > 0 && br != null) {
      brWeighted += br * sess;
      brSessions += sess;
    }
  }
  const engagementRate = brSessions > 0 ? 1 - (brWeighted / brSessions) : null;

  // Conversions
  const totalConversions = withGA4.reduce((s, a) => s + (a.ga4_conversions || 0), 0);
  const ga4Sessions = withGA4.reduce((s, a) => s + (a.ga4_sessions || 0), 0);
  const convRate = ga4Sessions > 0 ? totalConversions / ga4Sessions : null;

  // Best/worst read time
  const withReadTime = articles
    .filter(a => {
      const users = (a as any).ga4_total_users || 0;
      const dur = (a as any).ga4_engagement_duration || 0;
      return users > 0 && dur > 0;
    })
    .map(a => ({
      title: a.title,
      time: ((a as any).ga4_engagement_duration || 0) / ((a as any).ga4_total_users || 1),
    }))
    .sort((a, b) => b.time - a.time);

  const bestReadArticle = withReadTime.length > 0 ? withReadTime[0] : null;
  const worstReadArticle = withReadTime.length > 1 ? withReadTime[withReadTime.length - 1] : null;

  return {
    name,
    articles,
    totalClicks,
    totalImpressions,
    totalSessions,
    impressionsSeries,
    clicksSeries: clicksSeriesAgg,
    sessionsSeries: (() => {
      const s: number[] = [];
      for (let i = 0; i < maxLen; i++) {
        let dayTotal = 0;
        for (const a of articles) {
          dayTotal += ((a as any).ga4_sessions_series || [])[i] || 0;
        }
        s.push(dayTotal);
      }
      return s;
    })(),
    clicksDelta7d,
    positions,
    avgReadTime,
    scrollRate,
    engagementRate,
    totalAiSessions: articles.reduce((s, a) => s + ((a as any).ga4_ai_sessions || 0), 0),
    aiSourceBreakdown: (() => {
      const bySource: Record<string, number> = {};
      for (const a of articles) {
        const sources = (a as any).ga4_ai_sources;
        if (sources && typeof sources === 'object') {
          for (const [src, cnt] of Object.entries(sources)) {
            bySource[src] = (bySource[src] || 0) + Number(cnt || 0);
          }
        }
      }
      return Object.entries(bySource)
        .map(([source, sessions]) => ({ source, sessions }))
        .sort((a, b) => b.sessions - a.sessions);
    })(),
    totalAioImpressions: articles.reduce((s, a) => s + ((a as any).ai_overview_impressions || 0), 0),
    totalAioClicks: articles.reduce((s, a) => s + ((a as any).ai_overview_clicks || 0), 0),
    totalConversions,
    convRate,
    bestReadArticle,
    worstReadArticle,
    // Quality metrics
    medianClicks: computeMedian(articles.map(a => a.clicks || 0)),
    medianImpressions: computeMedian(articles.map(a => a.impressions || 0)),
    medianSessions: computeMedian(articles.map(a => a.ga4_sessions || 0)),
    clicksHitRate: articles.length > 0
      ? articles.filter(a => (a.clicks || 0) > siteMedianClicks).length / articles.length
      : 0,
    impressionsHitRate: articles.length > 0
      ? articles.filter(a => (a.impressions || 0) > siteMedianImpressions).length / articles.length
      : 0,
    // Scorecard
    ...(() => {
      const n = articles.length;
      const lowConfidence = n < 5;
      const impressionsPerArticle = n > 0 ? totalImpressions / n : 0;
      const sessionsPerArticle = n > 0 ? totalSessions / n : 0;
      const clusterCtr = totalImpressions > 0 ? totalClicks / totalImpressions : null;
      const highVisibility = impressionsPerArticle > siteMedianImprPerArticle;
      const highReach = sessionsPerArticle > siteMedianSessPerArticle;
      const highEfficiency = clusterCtr != null && clusterCtr > siteMedianCtr;

      let scorecardLabel: string;
      let scorecardColor: string;
      let scorecardExplanation: string;

      if (lowConfidence) {
        scorecardLabel = 'Insufficient data';
        scorecardColor = 'text-gray-400';
        scorecardExplanation = `Only ${n} article${n !== 1 ? 's' : ''} — too few to reliably assess this cluster. Publish more content here first.`;
      } else if (highVisibility && highEfficiency) {
        scorecardLabel = 'Strong — write more';
        scorecardColor = 'text-green-600';
        scorecardExplanation = 'Google shows these articles and users click through. This cluster resonates — invest in more content here.';
      } else if (highVisibility && !highEfficiency) {
        scorecardLabel = 'Visible but not compelling';
        scorecardColor = 'text-amber-600';
        scorecardExplanation = 'Google surfaces these articles but click-through is below average. Review titles, meta descriptions, and content hooks.';
      } else if (!highVisibility && highEfficiency) {
        scorecardLabel = 'Niche but effective';
        scorecardColor = 'text-cyan-600';
        scorecardExplanation = 'When people find these articles, they engage — but visibility is low. More content and better keyword targeting could expand reach.';
      } else {
        scorecardLabel = 'Needs attention';
        scorecardColor = 'text-red-500';
        scorecardExplanation = 'Low visibility and low efficiency. Review keyword targeting, content quality, and whether this cluster aligns with search demand.';
      }

      // Override if sessions tell a different story
      if (!lowConfidence && !highVisibility && highReach) {
        scorecardExplanation += ' Note: this cluster gets traffic from non-search sources (social, email, direct) — search isn\'t the only channel working.';
      }

      return { impressionsPerArticle, sessionsPerArticle, clusterCtr, lowConfidence, scorecardLabel, scorecardColor, scorecardExplanation };
    })(),
  };
}

function formatTime(seconds: number): string {
  const m = Math.floor(seconds / 60);
  const s = Math.round(seconds % 60);
  return `${m}m ${s}s`;
}

function fmt(n: number): string {
  if (n >= 10000) return `${(n / 1000).toFixed(0)}k`;
  if (n >= 1000) return `${(n / 1000).toFixed(1)}k`;
  return String(n);
}

// Position dot strip — shows each article as a dot on a 1–50+ scale
function PositionDotStrip({ positions }: { positions: { title: string; pos: number }[] }) {
  if (positions.length === 0) return <div className="text-[10px] text-gray-300">No position data</div>;
  const maxPos = 50;
  const ticks = [1, 5, 10, 20, 30, 40, 50];
  return (
    <div className="relative h-6 mt-1">
      {/* Scale line */}
      <div className="absolute top-2 left-0 right-0 h-px bg-gray-200" />
      {/* Tick labels */}
      <div className="absolute top-3 left-0 right-0 flex justify-between text-[8px] text-gray-300">
        {ticks.map(t => <span key={t}>{t}</span>)}
      </div>
      {/* Dots */}
      {positions.map((p, i) => {
        const clamped = Math.min(p.pos, maxPos);
        const pct = ((clamped - 1) / (maxPos - 1)) * 100;
        const color = p.pos <= 3 ? '#16a34a' : p.pos <= 10 ? '#01bcd4' : p.pos <= 20 ? '#f59e0b' : '#9ca3af';
        return (
          <div
            key={i}
            className="absolute top-0.5 w-2.5 h-2.5 rounded-full border border-white"
            style={{ left: `${pct}%`, backgroundColor: color, transform: 'translateX(-50%)' }}
            title={`${p.title}: position ${p.pos.toFixed(1)}`}
          />
        );
      })}
    </div>
  );
}

function ClusterCard({ cluster }: { cluster: ClusterData }) {
  return (
    <div className="border border-gray-200 rounded-lg p-4 bg-white">
      {/* Header */}
      <div className="flex items-center justify-between mb-3">
        <h3 className="text-sm font-semibold text-gray-900">{cluster.name || 'Uncategorized'}</h3>
        <span className="text-[10px] text-gray-400 bg-gray-50 px-2 py-0.5 rounded-full">
          {cluster.articles.length} article{cluster.articles.length !== 1 ? 's' : ''}
        </span>
      </div>

      {/* Three columns: Discovery | Engagement | Conversion */}
      <div className="grid grid-cols-3 gap-4 text-xs">
        {/* Discovery */}
        <div>
          <div className="text-[10px] font-medium text-gray-400 uppercase tracking-wide mb-2">Discovery</div>
          <div className="space-y-1">
            <div>
              <span className="text-gray-500">Impressions: </span>
              <span className="font-semibold text-gray-900">{fmt(cluster.totalImpressions)}</span>
            </div>
            <div>
              <span className="text-gray-500">Clicks: </span>
              <span className="font-semibold text-gray-900">{fmt(cluster.totalClicks)}</span>
            </div>
            <div>
              <span className={`font-medium ${cluster.clicksDelta7d.startsWith('↑') ? 'text-green-600' : cluster.clicksDelta7d.startsWith('↓') ? 'text-red-500' : 'text-gray-400'}`}>
                {cluster.clicksDelta7d}
              </span>
              <span className="text-gray-400 ml-1">vs prior 7d</span>
            </div>
            {cluster.totalAioImpressions > 0 && (
              <div>
                <span className="text-gray-500">AI Overview: </span>
                <span className="font-semibold text-blue-600">{fmt(cluster.totalAioImpressions)} impr</span>
              </div>
            )}
            {cluster.totalAiSessions > 0 && (
              <div>
                <span className="text-gray-500">AI Referral: </span>
                <span className="font-semibold text-emerald-600">{fmt(cluster.totalAiSessions)} sess</span>
              </div>
            )}
          </div>
        </div>

        {/* Engagement */}
        <div>
          <div className="text-[10px] font-medium text-gray-400 uppercase tracking-wide mb-2">Engagement</div>
          <div className="space-y-1">
            <div>
              <span className="text-gray-500">Avg Read: </span>
              <span className="font-semibold text-gray-900">{cluster.avgReadTime != null ? formatTime(cluster.avgReadTime) : '—'}</span>
            </div>
            <div>
              <span className="text-gray-500">Scroll 90%: </span>
              <span className="font-semibold text-gray-900">{cluster.scrollRate != null ? `${(cluster.scrollRate * 100).toFixed(0)}%` : '—'}</span>
            </div>
            <div>
              <span className="text-gray-500">Eng Rate: </span>
              <span className="font-semibold text-gray-900">{cluster.engagementRate != null ? `${(cluster.engagementRate * 100).toFixed(0)}%` : '—'}</span>
            </div>
          </div>
        </div>

        {/* Conversion */}
        <div>
          <div className="text-[10px] font-medium text-gray-400 uppercase tracking-wide mb-2">Conversion</div>
          <div className="space-y-1">
            <div>
              <span className="text-gray-500">Conversions: </span>
              <span className="font-semibold text-gray-900">{cluster.totalConversions}</span>
            </div>
            <div>
              <span className="text-gray-500">Conv Rate: </span>
              <span className="font-semibold text-gray-900">{cluster.convRate != null ? `${(cluster.convRate * 100).toFixed(2)}%` : '—'}</span>
            </div>
          </div>
        </div>
      </div>

      {/* Daily trend sparklines */}
      <div className="grid grid-cols-3 gap-4 mt-3 pt-3 border-t border-gray-100">
        <div>
          <div className="text-[10px] text-gray-400 mb-1">Impressions (28d)</div>
          <Sparkline values={cluster.impressionsSeries} width={160} height={32} />
        </div>
        <div>
          <div className="text-[10px] text-gray-400 mb-1">Clicks (28d)</div>
          <Sparkline values={cluster.clicksSeries} width={160} height={32} strokeColor="#16a34a" fillColor="rgba(22, 163, 74, 0.15)" />
        </div>
        <div>
          <div className="text-[10px] text-gray-400 mb-1">Sessions (28d)</div>
          <Sparkline values={cluster.sessionsSeries} width={160} height={32} strokeColor="#8b5cf6" fillColor="rgba(139, 92, 246, 0.15)" />
        </div>
      </div>

      {/* AI source breakdown */}
      {cluster.aiSourceBreakdown.length > 0 && (
        <div className="mt-3 pt-3 border-t border-gray-100">
          <div className="text-[10px] text-gray-400 mb-1.5">AI Referral Sources ({cluster.totalAiSessions} sessions)</div>
          <div className="space-y-1">
            {cluster.aiSourceBreakdown.slice(0, 5).map(({ source, sessions }) => (
              <div key={source} className="flex items-center gap-1.5">
                <div className="w-24 shrink-0 text-[10px] text-gray-500 truncate text-right">{source}</div>
                <div className="flex-1 bg-gray-50 rounded-full h-2.5 overflow-hidden">
                  <div
                    className="h-full rounded-full bg-emerald-500"
                    style={{ width: `${(sessions / cluster.aiSourceBreakdown[0].sessions) * 100}%` }}
                  />
                </div>
                <span className="text-[9px] font-mono text-gray-400 w-6 text-right">{sessions}</span>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Position dot strip */}
      <div className="mt-3 pt-3 border-t border-gray-100">
        <div className="text-[10px] text-gray-400 mb-1">Position by article</div>
        <PositionDotStrip positions={cluster.positions} />
      </div>

      {/* Best/worst callouts */}
      {(cluster.bestReadArticle || cluster.worstReadArticle) && (
        <div className="mt-3 pt-3 border-t border-gray-100 text-[10px] text-gray-500 space-y-0.5">
          {cluster.bestReadArticle && (
            <div>
              <span className="text-green-600 font-medium">Best read:</span>{' '}
              {formatTime(cluster.bestReadArticle.time)} ({cluster.bestReadArticle.title.slice(0, 50)}{cluster.bestReadArticle.title.length > 50 ? '…' : ''})
            </div>
          )}
          {cluster.worstReadArticle && (
            <div>
              <span className="text-amber-600 font-medium">Weakest:</span>{' '}
              {formatTime(cluster.worstReadArticle.time)} ({cluster.worstReadArticle.title.slice(0, 50)}{cluster.worstReadArticle.title.length > 50 ? '…' : ''})
            </div>
          )}
        </div>
      )}

      {/* Cluster scorecard */}
      <div className="mt-3 pt-3 border-t border-gray-100 space-y-2">
        <div className="flex items-center justify-between">
          <span className={`text-xs font-semibold ${cluster.scorecardColor}`}>{cluster.scorecardLabel}</span>
          <div className="flex gap-3 text-[10px] text-gray-400">
            <span>Median: {fmt(cluster.medianImpressions)} impr · {fmt(cluster.medianClicks)} clicks · {fmt(cluster.medianSessions)} sess</span>
            {cluster.clusterCtr != null && <span>CTR: {(cluster.clusterCtr * 100).toFixed(2)}%</span>}
          </div>
        </div>
        <div className="text-[10px] text-gray-500">{cluster.scorecardExplanation}</div>
      </div>
    </div>
  );
}

type ViewScale = 'total' | 'per-article';

export const ClusterCardView: React.FC<ClusterCardViewProps> = ({ groupBy, cohort = 'all' }) => {
  const [allRows, setAllRows] = useState<ArticlePerformanceRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [viewScale, setViewScale] = useState<ViewScale>('per-article');

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      try {
        const resp = await performanceMetricsService.list({
          days: 28,
          sortBy: 'clicks',
          order: 'desc',
          limit: 1000,
          contentType: 'post',
        });
        if (!cancelled) setAllRows(resp.metrics);
      } catch (e: any) {
        if (!cancelled) setError(e?.message || 'Failed to load data');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  const filteredRows = useMemo(() => filterByCohort(allRows, cohort), [allRows, cohort]);

  const clusters = useMemo(() => {
    // Site-wide medians for hit rate + scorecard
    const siteMedianClicks = computeMedian(filteredRows.map(r => r.clicks || 0));
    const siteMedianImpressions = computeMedian(filteredRows.map(r => r.impressions || 0));

    // Per-cluster medians need per-article values across the whole site
    // Group first to get cluster sizes, then compute per-article medians
    const groups: Record<string, ArticlePerformanceRow[]> = {};
    for (const row of filteredRows) {
      const key = (groupBy === 'pillar' ? row.pillar : row.funnel_stage) || 'Uncategorized';
      if (!groups[key]) groups[key] = [];
      groups[key].push(row);
    }

    // Site-wide per-article medians (for each cluster, impressions/n and sessions/n)
    const clusterImprPerArticle = Object.values(groups).map(articles => {
      const n = articles.length;
      return n > 0 ? articles.reduce((s, a) => s + (a.impressions || 0), 0) / n : 0;
    });
    const clusterSessPerArticle = Object.values(groups).map(articles => {
      const n = articles.length;
      return n > 0 ? articles.reduce((s, a) => s + (a.ga4_sessions || 0), 0) / n : 0;
    });
    const clusterCtrs = Object.values(groups).map(articles => {
      const imp = articles.reduce((s, a) => s + (a.impressions || 0), 0);
      const clk = articles.reduce((s, a) => s + (a.clicks || 0), 0);
      return imp > 0 ? clk / imp : 0;
    });

    const siteMedianImprPerArticle = computeMedian(clusterImprPerArticle);
    const siteMedianSessPerArticle = computeMedian(clusterSessPerArticle);
    const siteMedianCtr = computeMedian(clusterCtrs);

    return Object.entries(groups)
      .map(([name, articles]) => buildClusterData(name, articles, siteMedianClicks, siteMedianImpressions, siteMedianImprPerArticle, siteMedianSessPerArticle, siteMedianCtr))
      .sort((a, b) => b.totalClicks - a.totalClicks);
  }, [filteredRows, groupBy]);

  // Build cumulative series per cluster (supports total and per-article normalization)
  const cumulativeSeries = useMemo(() => {
    const normalize = viewScale === 'per-article';
    const impressions: { name: string; values: number[]; color: string }[] = [];
    const clicks: { name: string; values: number[]; color: string }[] = [];
    const sessions: { name: string; values: number[]; color: string }[] = [];
    clusters.forEach((c, i) => {
      const color = CLUSTER_COLORS[i % CLUSTER_COLORS.length];
      const divisor = normalize ? Math.max(c.articles.length, 1) : 1;
      const impCum: number[] = [];
      const clkCum: number[] = [];
      const sessCum: number[] = [];
      let impTotal = 0;
      let clkTotal = 0;
      let sessTotal = 0;
      const len = Math.max(c.impressionsSeries.length, c.clicksSeries.length, c.sessionsSeries.length);
      for (let d = 0; d < len; d++) {
        impTotal += (c.impressionsSeries[d] || 0) / divisor;
        clkTotal += (c.clicksSeries[d] || 0) / divisor;
        sessTotal += (c.sessionsSeries[d] || 0) / divisor;
        impCum.push(Math.round(impTotal));
        clkCum.push(Math.round(clkTotal * 10) / 10);
        sessCum.push(Math.round(sessTotal * 10) / 10);
      }
      impressions.push({ name: c.name, values: impCum, color });
      clicks.push({ name: c.name, values: clkCum, color });
      sessions.push({ name: c.name, values: sessCum, color });
    });
    return { impressions, clicks, sessions };
  }, [clusters, viewScale]);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-48">
        <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-cyan-500" />
      </div>
    );
  }

  if (error) {
    return <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded">{error}</div>;
  }

  return (
    <div>
      <div className="flex items-center justify-between mb-3">
        <div className="text-xs text-gray-400">
          {clusters.length} {groupBy === 'pillar' ? 'pillars' : 'funnel stages'} · {filteredRows.length} articles · sorted by total clicks
        </div>
        {/* Total vs Per Article toggle */}
        <div className="flex rounded border border-gray-300 overflow-hidden">
          {([['total', 'Total'], ['per-article', 'Per Article']] as const).map(([mode, label]) => (
            <button
              key={mode}
              onClick={() => setViewScale(mode)}
              className={`text-xs px-3 py-1 ${
                viewScale === mode
                  ? 'bg-gray-900 text-white'
                  : 'bg-white text-gray-600 hover:bg-gray-50'
              }`}
            >
              {label}
            </button>
          ))}
        </div>
      </div>

      {/* Six-panel overview: 3 cumulative lines + 3 bar charts */}
      {(() => {
        const getImpVal = (c: ClusterData) => viewScale === 'per-article' ? Math.round(c.totalImpressions / Math.max(c.articles.length, 1)) : c.totalImpressions;
        const getClkVal = (c: ClusterData) => viewScale === 'per-article' ? Math.round(c.totalClicks / Math.max(c.articles.length, 1)) : c.totalClicks;
        const getSessVal = (c: ClusterData) => viewScale === 'per-article' ? Math.round(c.totalSessions / Math.max(c.articles.length, 1)) : c.totalSessions;
        // Sort by impressions (first bar chart) — same order used for all three
        const barOrder = [...clusters].sort((a, b) => getImpVal(b) - getImpVal(a));
        return (
          <>
          {/* Row 1: Cumulative trend lines */}
          <div className="grid grid-cols-3 gap-4 mb-4">
            <div className="border border-gray-200 rounded-lg p-4 bg-white">
              <MultiLineChart
                series={cumulativeSeries.impressions}
                label={viewScale === 'per-article' ? 'Impressions / Article — Cumulative (28d)' : 'Impressions — Cumulative (28d)'}
                height={180}
              />
            </div>
            <div className="border border-gray-200 rounded-lg p-4 bg-white">
              <MultiLineChart
                series={cumulativeSeries.clicks}
                label={viewScale === 'per-article' ? 'Clicks / Article — Cumulative (28d)' : 'Clicks — Cumulative (28d)'}
                height={180}
              />
            </div>
            <div className="border border-gray-200 rounded-lg p-4 bg-white">
              <MultiLineChart
                series={cumulativeSeries.sessions}
                label={viewScale === 'per-article' ? 'Sessions / Article — Cumulative (28d)' : 'Sessions — Cumulative (28d)'}
                height={180}
              />
            </div>
          </div>
          {/* Row 2: Ranked bar charts (all use impressions sort order) */}
          <div className="grid grid-cols-3 gap-4 mb-4">
            <div className="border border-gray-200 rounded-lg p-4 bg-white">
              <RankingBarChart
                clusters={barOrder}
                getValue={getImpVal}
                color="#01bcd4"
                label={viewScale === 'per-article' ? 'Impressions / Article (28d)' : 'Impressions — Total (28d)'}
                preserveOrder
              />
            </div>
            <div className="border border-gray-200 rounded-lg p-4 bg-white">
              <RankingBarChart
                clusters={barOrder}
                getValue={getClkVal}
                color="#16a34a"
                label={viewScale === 'per-article' ? 'Clicks / Article (28d)' : 'Clicks — Total (28d)'}
                preserveOrder
              />
            </div>
            <div className="border border-gray-200 rounded-lg p-4 bg-white">
              <RankingBarChart
                clusters={barOrder}
                getValue={getSessVal}
                color="#8b5cf6"
                label={viewScale === 'per-article' ? 'Sessions / Article (28d)' : 'Sessions — Total (28d)'}
                preserveOrder
              />
            </div>
          </div>
          </>
        );
      })()}

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        {clusters.map((cluster) => (
          <ClusterCard key={cluster.name} cluster={cluster} />
        ))}
      </div>
    </div>
  );
};
